import React, { PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IIconBaseProps } from '../../common/interfaces';
import { Icon } from '../../components/Icon/Icon';
import { Heading5 } from '../../components/Headings/Headings';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { P } from '../../components/Texts/Texts';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterCss } from '../../fundamentals/spaces/space';
import { StatusIcon } from '../../components/StatusIcon/StatusIcon';
import { STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { MATERIAL_ICONS, MATERIAL_ICONS_TYPES } from '../../components/Icon/icons';
import { IProgressIndicatorBaseProps, ProgressIndicatorContext } from './ProgressIndicatorContext';
import { resolveProps } from '../../common/utils';
import { BaseDiv } from '../../common/BaseElements';

export const PROGRESS_INDICATOR_STATUS = {
  COMPLETED: 'completed',
  CURRENT: 'current',
  PENDING: 'pending'
} as const;

export type ProgressIndicatorStatus = (typeof PROGRESS_INDICATOR_STATUS)[keyof typeof PROGRESS_INDICATOR_STATUS];

export interface IProgressIndicatorStepProps extends IIconBaseProps, IProgressIndicatorBaseProps {
  position?: string | number;
  /* Required. From Figma: `description` cannot be used alone without title */
  title: string;
  description?: string;
  status?: ProgressIndicatorStatus;
  disabled?: boolean;
}

type ProgressCssProps = Required<Pick<IProgressIndicatorStepProps, 'status'>>;

const ProgressIndicatorStepContainer = styled(BaseDiv)`
  flex: 1;
`;
const Step = styled(GapContainer)<ProgressCssProps & IGutterProps>`
  ${useGutterCss()}

  border-top: ${({ theme, status }) => theme.progressIndicatorColours[status].border};
`;
const LeftStatus = styled(GapContainer)`
  margin-left: auto;
`;
const Content = styled(BaseDiv)``;
const Position = styled(Heading5)<ProgressCssProps>`
  color: ${({ theme, status }) => theme.progressIndicatorColours[status].text};
`;

const StepStatusIcon = styled(StatusIcon)`
  margin-left: auto;
`;

export const STATUS_TO_ICON = {
  [PROGRESS_INDICATOR_STATUS.COMPLETED]: {
    variant: STATUS_COLOURS.SUCCESS,
    name: MATERIAL_ICONS.CheckCircle
  },
  [PROGRESS_INDICATOR_STATUS.CURRENT]: {
    variant: STATUS_COLOURS.INFO,
    name: MATERIAL_ICONS.Timelapse,
    type: MATERIAL_ICONS_TYPES.OUTLINE
  },
  [PROGRESS_INDICATOR_STATUS.PENDING]: {
    variant: STATUS_COLOURS.NEUTRAL,
    name: MATERIAL_ICONS.WatchLater,
    type: MATERIAL_ICONS_TYPES.OUTLINE
  }
};

export const ProgressIndicatorStep: React.FC<PropsWithChildren<IProgressIndicatorStepProps>> = (props) => {
  const contextProps = React.useContext(ProgressIndicatorContext);
  const {
    icon,
    iconType,
    title,
    description,
    position = 1,
    isIconSmall = false,
    status = 'current',
    showStatusIcon,
    isCompact,
    isMobile,
    hidePosition,
    children,
    sheetTitle,
    t,
    ...rest
  } = resolveProps({ ...contextProps }, { ...props });

  const { generateTestId } = useTestId(rest, 'ProgressIndicatorStep');
  const theme = useTheme();

  const showDescription = !isCompact && description;
  const showIcon = icon && (!isCompact || (isCompact && isMobile));
  const iconSize = isIconSmall || (!isIconSmall && !description) || isMobile ? undefined : SIZES.LARGE;

  return (
    <ProgressIndicatorStepContainer {...generateTestId()} {...rest}>
      {position !== undefined && !hidePosition && (
        <Position status={status} gutterBottom={8}>
          {position}
        </Position>
      )}
      <Step gap={12} gutterTop={12} status={status}>
        {showIcon && <Icon colour={theme.progressIndicatorColours[status].text} size={iconSize} name={icon} type={iconType} />}
        <Content>
          {title && (
            <P fontWeightCss='semiBold' gutterBottom={false}>
              {title}
            </P>
          )}
          {showDescription && <P gutterBottom={false}>{description}</P>}
        </Content>
        <LeftStatus gap={8}>
          {isMobile && children}
          {(status === PROGRESS_INDICATOR_STATUS.COMPLETED || showStatusIcon || isMobile) && <StepStatusIcon {...STATUS_TO_ICON[status]} />}
        </LeftStatus>
      </Step>
    </ProgressIndicatorStepContainer>
  );
};
